import { makeAutoObservable, runInAction, computed } from "mobx";
import { activityLogService } from "../services";

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SORT = { key: "created_at", direction: "desc" };
const createFilters = () => ({
  module: "All",
  searchText: "",
  startDate: "",
  endDate: "",
  selectedUserId: "All",
});

const normalizeLog = (log = {}) => ({
  id: log._id || log.id || "",
  user: log.user || log.userId || null,
  module: log.module || "",
  moduleTitle: log.moduleTitle || "",
  action: log.action || "",
  details: log.details || "",
  createdAt: log.created_at || log.createdAt || "",
  raw: log,
});

class ActivityLogStore {
  logs = [];
  total = 0;
  loading = false;
  error = "";
  page = 1;
  pageSize = DEFAULT_PAGE_SIZE;
  sort = DEFAULT_SORT;
  filters = createFilters();

  users = [];
  usersLoading = false;
  usersError = "";
  usersLoaded = false;

  detailsOpen = false;
  detailsText = "";

  constructor() {
    makeAutoObservable(this, {
      hasLogs: computed,
    });
  }

  get hasLogs() {
    return this.logs.length > 0;
  }

  setFilters = (patch) => {
    this.filters = { ...this.filters, ...patch };
    this.page = 1;
  };

  resetFilters = () => {
    this.filters = createFilters();
    this.page = 1;
  };

  setPage = (page) => {
    this.page = page;
  };

  setPageSize = (size) => {
    this.pageSize = size;
  };

  setSort = (sort) => {
    this.sort = { ...this.sort, ...sort };
  };

  openDetails = (details) => {
    const resolved =
      typeof details === "string"
        ? details
        : details == null
          ? ""
          : JSON.stringify(details, null, 2);
    this.detailsText = resolved;
    this.detailsOpen = true;
  };

  closeDetails = () => {
    this.detailsOpen = false;
    this.detailsText = "";
  };

  buildParams = ({ userId, companyId, isAdminRole, searchText }) => {
    const params = {
      userId,
      companyId,
      page: this.page,
      limit: this.pageSize,
      sortField: this.sort.key,
      sortOrder: this.sort.direction,
    };

    if (isAdminRole) {
      params.selectedUserId = this.filters.selectedUserId;
    }

    if (this.filters.module && this.filters.module !== "All") {
      params.module = this.filters.module;
    }

    if (this.filters.startDate) {
      params.startDate = this.filters.startDate;
    }

    if (this.filters.endDate) {
      params.endDate = this.filters.endDate;
    }

    const resolvedSearch = searchText ?? this.filters.searchText;
    if (resolvedSearch) {
      params.searchText = resolvedSearch;
    }

    return params;
  };

  loadLogs = async ({ userId, companyId, isAdminRole, searchText } = {}) => {
    if (!userId) return;

    runInAction(() => {
      this.loading = true;
      this.error = "";
    });

    try {
      const params = this.buildParams({
        userId,
        companyId,
        isAdminRole,
        searchText,
      });
      const data = await activityLogService.listLogs(params);

      if (!data) {
        runInAction(() => {
          this.logs = [];
          this.total = 0;
          this.error = "Unable to load activity logs.";
        });
        return;
      }

      const items = Array.isArray(data?.items) ? data.items : [];
      const pagination = data?.pagination || {};

      runInAction(() => {
        this.logs = items.map((item) => normalizeLog(item));
        this.total = Number.isFinite(pagination.total)
          ? pagination.total
          : this.logs.length;
      });
    } catch (error) {
      runInAction(() => {
        this.logs = [];
        this.total = 0;
        this.error = error?.message || "Unable to load activity logs.";
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  loadUsers = async () => {
    runInAction(() => {
      this.usersLoading = true;
      this.usersError = "";
    });

    try {
      const data = await activityLogService.listUsers();
      if (!data) {
        runInAction(() => {
          this.users = [];
          this.usersError = "Unable to load users.";
        });
        return;
      }

      const users = Array.isArray(data?.users) ? data.users : [];

      runInAction(() => {
        this.users = users;
      });
    } catch (error) {
      runInAction(() => {
        this.users = [];
        this.usersError = error?.message || "Unable to load users.";
      });
    } finally {
      runInAction(() => {
        this.usersLoading = false;
        this.usersLoaded = true;
      });
    }
  };
}

export const activityLogStore = new ActivityLogStore();
